import { lazy, FC, Suspense } from "react";
import { Route, Routes, Navigate } from "react-router-dom";
import { MasterLayout } from "../../_metronic/layout/MasterLayout";
import TopBarProgress from "react-topbar-progress-indicator";
import { getCSSVariableValue } from "../../_metronic/assets/ts/_utils";
import { WithChildren } from "../../_metronic/helpers";

const PrivateRoutes = () => {
  const UsersList = lazy(() => import("../modules/users/pages/UsersList"));
  const UsersCreate = lazy(() => import("../modules/users/pages/UsersCreate"));
  const UsersEdit = lazy(() => import("../modules/users/pages/UsersEdit"));
  const PermissionsCreate = lazy(
    () => import("../modules/permissions/pages/PermissionsCreate"),
  );
  const PermissionsList = lazy(
    () => import("../modules/permissions/pages/PermissionsList"),
  );
  const PermissionsEdit = lazy(
    () => import("../modules/permissions/pages/PermissionsEdit"),
  );
  const RolesList = lazy(() => import("../modules/users/pages/RolesList"));
  const RolesCreate = lazy(() => import("../modules/users/pages/RolesCreate"));
  const RolesEdit = lazy(() => import("../modules/users/pages/RolesEdit"));
  const UserRoles = lazy(() => import("../modules/users/pages/UserRoles"));
  const UserPermissions = lazy(
    () => import("../modules/users/pages/UserPermissions"),
  );
  const RolePermissions = lazy(
    () => import("../modules/users/pages/RolePermissions"),
  );
  const CompaniesDashboard = lazy(
    () => import("../modules/companies/pages/CompaniesDashboard"),
  );
  const CompaniesCreate = lazy(
    () => import("../modules/companies/pages/CompaniesCreate"),
  );
  const ContractsList = lazy(
    () => import("../modules/companies/contracts/pages/ContractsList"),
  );
  const ContractsCreate = lazy(
    () => import("../modules/companies/contracts/pages/ContractsCreate"),
  );
  const ContractsEdit = lazy(
    () => import("../modules/companies/contracts/pages/ContractsEdit"),
  );
  const ChargesList = lazy(
    () => import("../modules/companies/contracts/charges/pages/ChargesList"),
  );
  const PlanUpgradesList = lazy(
    () => import("../modules/companies/plan-upgrades/pages/PlanUpgradesList"),
  );
  const DiscountCouponsList = lazy(
    () => import("../modules/discount-coupons/pages/DiscountCouponsList"),
  );
  const DiscountCouponsCreate = lazy(
    () => import("../modules/discount-coupons/pages/DiscountCouponsCreate"),
  );
  const DiscountCouponsEdit = lazy(
    () => import("../modules/discount-coupons/pages/DiscountCouponsEdit"),
  );
  const DiscountsList = lazy(
    () => import("../modules/discounts/pages/DiscountList"),
  );
  const DiscountsCreate = lazy(
    () => import("../modules/discounts/pages/DiscountCreate"),
  );
  const DiscountsEdit = lazy(
    () => import("../modules/discounts/pages/DiscountEdit"),
  );
  const UserContracts = lazy(
    () => import("../modules/users/pages/UserContracts"),
  );
  const CompaniesEdit = lazy(
    () => import("../modules/companies/pages/CompaniesEdit"),
  );
  const PlansCreate = lazy(() => import("../modules/plans/pages/PlansCreate"));
  const PlansList = lazy(() => import("../modules/plans/pages/PlansList"));
  const PlansEdit = lazy(() => import("../modules/plans/pages/PlansEdit"));
  const PlansDiscountCoupons = lazy(
    () => import("../modules/plans/pages/PlansDiscountCoupons"),
  );
  const LocationsList = lazy(
    () => import("../modules/companies/locations/pages/LocationsList"),
  );
  const LocationsCreate = lazy(
    () => import("../modules/companies/locations/pages/LocationsCreate"),
  );
  const LocationsEdit = lazy(
    () => import("../modules/companies/locations/pages/LocationsEdit"),
  );
  // const WidgetsPage = lazy(() => import('../modules/widgets/WidgetsPage'))
  // const ChatPage = lazy(() => import('../modules/apps/chat/ChatPage'))
  // const UsersPage = lazy(() => import('../modules/apps/user-management/UsersPage'))
  const TemplatesCreate = lazy(
    () => import("../modules/templates/pages/TemplatesCreate"),
  );
  const TemplatesList = lazy(
    () => import("../modules/templates/pages/TemplatesList"),
  );
  const TemplatesEdit = lazy(
    () => import("../modules/templates/pages/TemplatesEdit"),
  );
  const ModulesCreate = lazy(() => import("../modules/modules/ModulesCreate"));
  const ModulesList = lazy(() => import("../modules/modules/ModulesList"));
  const ModulesEdit = lazy(() => import("../modules/modules/ModulesEdit"));
  const ProfilePage = lazy(
    () => import("../modules/profile/pages/ProfilePage"),
  );
  // const ProfilePageSettings = lazy(
  //   () => import("../modules/profile/pages/ProfilePageSettings")
  // );

  // const NewProfilePage = lazy(
  //   () => import("../modules/profile/ProfilePage")
  // );
  const SectionsList = lazy(
    () => import("../modules/companies/locations/sections/pages/SectionsList"),
  );
  const SectionsCreate = lazy(
    () =>
      import("../modules/companies/locations/sections/pages/SectionsCreate"),
  );
  const SectionsEdit = lazy(
    () => import("../modules/companies/locations/sections/pages/SectionsEdit"),
  );

  const BraceletColorsList = lazy(
    () =>
      import("../modules/companies/bracelet-colors/pages/BraceletColorsList"),
  );
  const BraceletColorsCreate = lazy(
    () =>
      import("../modules/companies/bracelet-colors/pages/BraceletColorsCreate"),
  );
  const BraceletColorsEdit = lazy(
    () =>
      import("../modules/companies/bracelet-colors/pages/BraceletColorsEdit"),
  );
  const BraceletsList = lazy(
    () => import("../modules/companies/bracelets/pages/BraceletsList"),
  );
  const BraceletsCreate = lazy(
    () => import("../modules/companies/bracelets/pages/BraceletsCreate"),
  );
  const BraceletsEdit = lazy(
    () => import("../modules/companies/bracelets/pages/BraceletsEdit"),
  );
  const BraceletsOrders = lazy(
    () => import("../modules/companies/bracelets/pages/BraceletsOrders"),
  );
  const AccessPointsList = lazy(
    () =>
      import(
        "../modules/companies/locations/access-points/pages/AccessPointsList"
      ),
  );
  const AccessPointsCreate = lazy(
    () =>
      import(
        "../modules/companies/locations/access-points/pages/AccessPointsCreate"
      ),
  );
  const AccessPointsEdit = lazy(
    () =>
      import(
        "../modules/companies/locations/access-points/pages/AccessPointsEdit"
      ),
  );

  const BraceletColorsLocations = lazy(
    () =>
      import(
        "../modules/companies/bracelet-colors/pages/BraceletColorsLocations"
      ),
  );

  const BraceletColorsSections = lazy(
    () =>
      import(
        "../modules/companies/bracelet-colors/pages/BraceletColorsSections"
      ),
  );

  const TagsList = lazy(() => import("../modules/tags/pages/TagsList"));
  const TagsCreate = lazy(() => import("../modules/tags/pages/TagsCreate"));
  const TagsEdit = lazy(() => import("../modules/tags/pages/TagsEdit"));
  const TemplateTags = lazy(
    () => import("../modules/templates/pages/TemplateTags"),
  );

  return (
    <Routes>
      <Route element={<MasterLayout />}>
        <Route path="auth/*" element={<Navigate to="/companies" />} />
        {/* Pages */}
        <Route path="companies" element={<CompaniesDashboard />} />
        {/* <Route path='builder' element={<BuilderPageWrapper />} /> */}
        {/* <Route path='menu-test' element={<MenuTestPage />} /> */}
        {/* Lazy Modules */}
        <Route
          path="/users"
          element={
            <SuspensedView>
              <UsersList />
            </SuspensedView>
          }
        />
        <Route
          path="/users/create"
          element={
            <SuspensedView>
              <UsersCreate />
            </SuspensedView>
          }
        />
        <Route
          path="/users/:id"
          element={
            <SuspensedView>
              <UsersEdit />
            </SuspensedView>
          }
        />
        <Route
          path="/users/:id/contracts"
          element={
            <SuspensedView>
              <UserContracts />
            </SuspensedView>
          }
        />
        <Route
          path="/users/:id/roles"
          element={
            <SuspensedView>
              <UserRoles />
            </SuspensedView>
          }
        />
        <Route
          path="/users/:id/permissions"
          element={
            <SuspensedView>
              <UserPermissions />
            </SuspensedView>
          }
        />
        <Route
          path="/roles"
          element={
            <SuspensedView>
              <RolesList />
            </SuspensedView>
          }
        />
        <Route
          path="/roles/create"
          element={
            <SuspensedView>
              <RolesCreate />
            </SuspensedView>
          }
        />
        <Route
          path="/roles/:id"
          element={
            <SuspensedView>
              <RolesEdit />
            </SuspensedView>
          }
        />
        <Route
          path="/roles/:id/permissions"
          element={
            <SuspensedView>
              <RolePermissions />
            </SuspensedView>
          }
        />
        <Route
          path="/companies/"
          element={
            <SuspensedView>
              <CompaniesDashboard />
            </SuspensedView>
          }
        />
        <Route
          path="/companies/:companyId/contracts"
          element={
            <SuspensedView>
              <ContractsList />
            </SuspensedView>
          }
        />
        <Route
          path="/companies/:companyId/contracts/create"
          element={
            <SuspensedView>
              <ContractsCreate />
            </SuspensedView>
          }
        />
        <Route
          path="/companies/:companyId/contracts/:id"
          element={
            <SuspensedView>
              <ContractsEdit />
            </SuspensedView>
          }
        />
        <Route
          path="/companies/:companyId/contracts/:contractId/charges"
          element={
            <SuspensedView>
              <ChargesList />
            </SuspensedView>
          }
        />
        <Route
          path="/plan-upgrades"
          element={
            <SuspensedView>
              <PlanUpgradesList />
            </SuspensedView>
          }
        />
        <Route
          path="/companies/:id"
          element={
            <SuspensedView>
              <CompaniesEdit />
            </SuspensedView>
          }
        />
        <Route
          path="/companies/create"
          element={
            <SuspensedView>
              <CompaniesCreate />
            </SuspensedView>
          }
        />
        <Route
          path="/companies/:companyId/locations"
          element={
            <SuspensedView>
              <LocationsList />
            </SuspensedView>
          }
        />
        <Route
          path="/companies/:companyId/locations/create"
          element={
            <SuspensedView>
              <LocationsCreate />
            </SuspensedView>
          }
        />
        <Route
          path="/companies/:companyId/locations/:locationId"
          element={
            <SuspensedView>
              <LocationsEdit />
            </SuspensedView>
          }
        />
        <Route
          path="/plans"
          element={
            <SuspensedView>
              <PlansList />
            </SuspensedView>
          }
        />
        <Route
          path="/plans/create"
          element={
            <SuspensedView>
              <PlansCreate />
            </SuspensedView>
          }
        />
        <Route
          path="/plans/:id"
          element={
            <SuspensedView>
              <PlansEdit />
            </SuspensedView>
          }
        />
        <Route
          path="/plans/:id/discount-coupons"
          element={
            <SuspensedView>
              <PlansDiscountCoupons />
            </SuspensedView>
          }
        />
        <Route
          path="/permissions"
          element={
            <SuspensedView>
              <PermissionsList />
            </SuspensedView>
          }
        />
        <Route
          path="/permissions/create"
          element={
            <SuspensedView>
              <PermissionsCreate />
            </SuspensedView>
          }
        />
        <Route
          path="/permissions/:id"
          element={
            <SuspensedView>
              <PermissionsEdit />
            </SuspensedView>
          }
        />
        <Route
          path="/discount-coupons"
          element={
            <SuspensedView>
              <DiscountCouponsList />
            </SuspensedView>
          }
        />
        <Route
          path="/discount-coupons/create"
          element={
            <SuspensedView>
              <DiscountCouponsCreate />
            </SuspensedView>
          }
        />
        <Route
          path="/discount-coupons/:id"
          element={
            <SuspensedView>
              <DiscountCouponsEdit />
            </SuspensedView>
          }
        />
        <Route
          path="/discounts"
          element={
            <SuspensedView>
              <DiscountsList />
            </SuspensedView>
          }
        />
        <Route
          path="/discounts/create"
          element={
            <SuspensedView>
              <DiscountsCreate />
            </SuspensedView>
          }
        />
        <Route
          path="/discounts/:id"
          element={
            <SuspensedView>
              <DiscountsEdit />
            </SuspensedView>
          }
        />
        <Route
          path="/templates"
          element={
            <SuspensedView>
              <TemplatesList />
            </SuspensedView>
          }
        />
        <Route
          path="/templates/create"
          element={
            <SuspensedView>
              <TemplatesCreate />
            </SuspensedView>
          }
        />
        <Route
          path="/templates/:id"
          element={
            <SuspensedView>
              <TemplatesEdit />
            </SuspensedView>
          }
        />
        <Route
          path="/tags"
          element={
            <SuspensedView>
              <TagsList />
            </SuspensedView>
          }
        />
        <Route
          path="/tags/create"
          element={
            <SuspensedView>
              <TagsCreate />
            </SuspensedView>
          }
        />
        <Route
          path="/tags/:id"
          element={
            <SuspensedView>
              <TagsEdit />
            </SuspensedView>
          }
        />
        <Route
          path="/templates/:id/tags"
          element={
            <SuspensedView>
              <TemplateTags />
            </SuspensedView>
          }
        />
        <Route
          path="/modules"
          element={
            <SuspensedView>
              <ModulesList />
            </SuspensedView>
          }
        />
        <Route
          path="/profile"
          element={
            <SuspensedView>
              <ProfilePage />
            </SuspensedView>
          }
        />
        <Route
          path="/modules/create"
          element={
            <SuspensedView>
              <ModulesCreate />
            </SuspensedView>
          }
        />
        <Route
          path="/modules/:id"
          element={
            <SuspensedView>
              <ModulesEdit />
            </SuspensedView>
          }
        />
        <Route
          path="/companies/:companyId/locations/:locationId/sections"
          element={
            <SuspensedView>
              <SectionsList />
            </SuspensedView>
          }
        />
        <Route
          path="/companies/:companyId/locations/:locationId/sections/create"
          element={
            <SuspensedView>
              <SectionsCreate />
            </SuspensedView>
          }
        />
        <Route
          path="/companies/:companyId/locations/:locationId/sections/:id"
          element={
            <SuspensedView>
              <SectionsEdit />
            </SuspensedView>
          }
        />
        <Route
          path="/companies/:companyId/bracelet-colors"
          element={
            <SuspensedView>
              <BraceletColorsList />
            </SuspensedView>
          }
        />
        <Route
          path="/companies/:companyId/bracelet-colors/create"
          element={
            <SuspensedView>
              <BraceletColorsCreate />
            </SuspensedView>
          }
        />
        <Route
          path="/companies/:companyId/bracelet-colors/:id"
          element={
            <SuspensedView>
              <BraceletColorsEdit />
            </SuspensedView>
          }
        />
        <Route
          path="/companies/:companyId/bracelet-colors/:id/locations"
          element={
            <SuspensedView>
              <BraceletColorsLocations />
            </SuspensedView>
          }
        />
        <Route
          path="/companies/:companyId/bracelet-colors/:id/sections"
          element={
            <SuspensedView>
              <BraceletColorsSections />
            </SuspensedView>
          }
        />
        <Route
          path="/companies/:companyId/bracelets"
          element={
            <SuspensedView>
              <BraceletsList />
            </SuspensedView>
          }
        />
        <Route
          path="/companies/:companyId/bracelets/create"
          element={
            <SuspensedView>
              <BraceletsCreate />
            </SuspensedView>
          }
        />
        <Route
          path="/companies/:companyId/bracelets/:id"
          element={
            <SuspensedView>
              <BraceletsEdit />
            </SuspensedView>
          }
        />
        <Route
          path="/companies/:companyId/bracelets/orders"
          element={
            <SuspensedView>
              <BraceletsOrders />
            </SuspensedView>
          }
        />
        <Route
          path="/companies/:companyId/bracelets/imports/:importId/bracelets"
          element={
            <SuspensedView>
              <BraceletsList />
            </SuspensedView>
          }
        />
        <Route
          path="/companies/:companyId/locations/:locationId/sections/:sectionId/access-points"
          element={
            <SuspensedView>
              <AccessPointsList />
            </SuspensedView>
          }
        />
        <Route
          path="/companies/:companyId/locations/:locationId/sections/:sectionId/access-points/create"
          element={
            <SuspensedView>
              <AccessPointsCreate />
            </SuspensedView>
          }
        />
        <Route
          path="/companies/:companyId/locations/:locationId/sections/:sectionId/access-points/:id"
          element={
            <SuspensedView>
              <AccessPointsEdit />
            </SuspensedView>
          }
        />
        {/* Page Not Found */}
        <Route path="*" element={<Navigate to="/error/404" />} />
      </Route>
    </Routes>
  );
};

const SuspensedView: FC<WithChildren> = ({ children }) => {
  const baseColor = getCSSVariableValue("--bs-primary");
  TopBarProgress.config({
    barColors: {
      "0": baseColor,
    },
    barThickness: 1,
    shadowBlur: 5,
  });
  return <Suspense fallback={<TopBarProgress />}>{children}</Suspense>;
};

export { PrivateRoutes };
